
    // IMPORTS
    import { defineComponent, computed, watchEffect, provide, onBeforeUnmount } from 'vue'
    import router from '@/router'
    import { useRoute } from 'vue-router'
    import { useSparePartsStore } from '@/store/spareParts'
    import { useEquipmentStore } from '@/store/equipment'
    import { useMaterialHistoryStore } from '@/store/materialHistory'
    import { useUserStore } from '@/store/user'

    import { Card, CardHeader, CardTitle, CardBody } from '@progress/kendo-vue-layout'
    import PortalPage from '@/components/common/portal-page.vue'
    import DocLink from '@/components/common/doc-link.vue'
    import StockIndicator from '@/components/common/stock-indicator.vue'
    import PurchaseButton from '@/components/common/purchase-button.vue'
    import MyIcon from '@/components/common/my-icon.vue'
    import MaterialLinkSection from './material-link-section.vue'
    import LoaderGuard from '@/components/common/loader-guard.vue'
    import MaterialLink from '@/components/common/material-link.vue'
    import MaterialPicture from '@/components/common/material-picture.vue'

    import { buildBomItemLocalization, buildLocalization, buildNumberFormatter, buildUnitLocalization, navigateToFallbackRoute, StringUtil } from '@/util'
    import { BomTreeNodesToMaterialItemListTransformer } from '@/lib/bom'
    import { DocumentVisibility, DocumentNames } from '@/types/document'
    import { buildPriceFormatter, MaterialListItem } from '@/types/material'
    import { IBomHeaderDTO } from '@/types/bom'

    export default defineComponent({
        name: 'MaterialView',

        components: {
            PortalPage,
            LoaderGuard,
            MaterialPicture,
            MaterialLink,
            DocLink,
            Card,
            CardHeader,
            CardTitle,
            CardBody,
            StockIndicator,
            PurchaseButton,
            MyIcon,
            MaterialLinkSection
        },

        /********************************************************************************************************************
         PUBLIC PROPERTIES
         ********************************************************************************************************************/
        setup() {
            /********************************************************************************************************************
             PRIVATE DATA
             ********************************************************************************************************************/
            const userStore = useUserStore()
            const sparePartsStore = useSparePartsStore()
            const equipmentStore = useEquipmentStore()
            const route = useRoute()
            const bomTransformer = new BomTreeNodesToMaterialItemListTransformer()
            //const whereUsedParentBoms: string[] = [ DbConstants.materialBomUsage.Default, DbConstants.materialBomUsage.Maintenance ] -- decision 2024-05-07: Use all BOM usages for "where used"

            let unregisterGoAllBackHook: (() => void) | null = null

            /********************************************************************************************************************
             COMPUTED PROPERTIES
             ********************************************************************************************************************/
            // Get material number from parameterized URL segment 'matno'
            const serialNo = computed(() => route.params.sn as string)
            const equipment = computed(() => equipmentStore.masterData)
            const materialNo = computed(() => parseInt(route.params.matno as string))
            const isValidMaterialNo = computed(() => !isNaN(materialNo.value))
            const isEquipmentMode = computed(() => StringUtil.hasContent(serialNo.value) && equipment.value?.noBomSync !== true)
            const pageTitle = computed(() => description.value == null
                ? localize('Loading')
                : isEquipmentMode.value && equipment.value?.description
                    ? `${serialNo.value} ${equipment.value.description}`
                    : materialText(materialData.value)
            )
            const { priceFormatter } = buildPriceFormatter()
            const numberFormatter = buildNumberFormatter()
            const loginType = computed(() => userStore.loginType)
            const isAdmin = computed(() => userStore.isAdmin)

            const bomHeaderData = computed(() => sparePartsStore.spareParts?.find(part => part.material.materialNo == materialNo.value))
            const parentMaterials = computed(() => getEffectiveItems(collectParentMaterials(sparePartsStore.spareParts)))

            const hasLoaded = computed(() => bomHeaderData.value != null && unregisterGoAllBackHook == null)

            const materialData = computed(() => bomHeaderData.value
                ? bomTransformer.transformSingle(bomHeaderData.value)
                : null)
            const effectiveRelatedItems = computed(() => getEffectiveItems(materialData.value?.relatedItems))
            const description = computed(() => materialData.value?.description)
            const salesText = computed(() => materialData.value?.salesText
                ?.replaceAll('\r', '')
                ?.replaceAll('\n', '<br>'))
            const qtyOnEquipment = computed(() => isEquipmentMode.value ? getQtyOnEquipment() : null)
            const additionalDocs = computed(() => materialData.value?.documents?.filter(doc => doc.documentType != 'RES'))
            const namesOfAdditionalDocs = computed(() => additionalDocs.value?.length ? new DocumentNames(additionalDocs.value).localizedNamesOfDocuments() : undefined)
            const additionalDocsForEveryone = computed(() => namesOfAdditionalDocs.value?.filter(doc => doc.visibility == DocumentVisibility.Everyone))
            const additionalDocsForAdmins = computed(() => namesOfAdditionalDocs.value?.filter(doc => doc.visibility == DocumentVisibility.AdminsOnly))
            const priceData = computed(() => materialData.value ? priceFormatter.value(materialData.value).formattedPriceWithUnit() : null)
            const documentsForAdminsSectionTitle = computed(() => `${localize('documents')} (${localize('adminData')?.toLowerCase()})`)
            const showOrderingSection = computed(() => loginType.value == 'user' && materialData.value?.isSparePart)
            const showPrice = computed(() => userStore.userData?.permissions?.priceAccessAllowed === true)
            const currentRouteName = computed(() => router.currentRoute.value.name ?? 'material')

            const materialHistoryItems = computed(() => materialHistoryStore.recentlyViewed
                .filter(item => item.materialNo != materialNo.value)
            )

            const computedData = {
                materialNo,
                isEquipmentMode,
                pageTitle,
                priceFormatter,
                numberFormatter,
                loginType,
                isAdmin,
                bomHeaderData,
                parentMaterials,
                hasLoaded,
                materialData,
                effectiveRelatedItems,
                description,
                salesText,
                qtyOnEquipment,
                additionalDocs,
                namesOfAdditionalDocs,
                additionalDocsForEveryone,
                additionalDocsForAdmins,
                priceData,
                documentsForAdminsSectionTitle,
                showOrderingSection,
                showPrice,
                materialHistoryItems
            }

            /********************************************************************************************************************
             WATCHERS
             ********************************************************************************************************************/
            watchEffect(() => {
                if (!isValidMaterialNo.value) {
                    router.push("/")
                }
            })

            /********************************************************************************************************************
             PUBLIC METHODS AND EVENT HANDLERS
             ********************************************************************************************************************/
            const { localize } = buildLocalization()
            const { localizeUnit3 } = buildUnitLocalization()
            const { localizeBomItemText } = buildBomItemLocalization()

            function onClick(item: MaterialListItem) {
                scrollToTop()
                router.push({ name: currentRouteName.value, params: { matno: item.materialNo, sn: serialNo.value } })
            }

            function materialText(item: MaterialListItem | null) {
                return item == null
                    ? ''
                    : `${item.materialNo} ${item.description}`
            }

            function goAllBack() {
                // If there's no in-app history at all, go directly to fallback
                if (!window.history.state?.back) {
                    navigateToFallbackRoute(router, route)
                    return
                }

                // register router hook to keep going back until a different route is reached
                unregisterGoAllBackHook = router.afterEach((to, from) => {
                    //DEBUG(`### GUARD: ${from.path} (${from.name?.toString()}) --> ${to.path} (${to.name?.toString()})`)

                    if (from.name != to.name) {
                        if (unregisterGoAllBackHook != null) {
                            unregisterGoAllBackHook() // redirect to different URL succeeded
                        }
                    }
                    else if (!window.history.state?.back) {
                        // No more in-app history — navigate to fallback route
                        if (unregisterGoAllBackHook != null) {
                            unregisterGoAllBackHook()
                        }
                        navigateToFallbackRoute(router, route)
                    }
                    else {
                        router.go(-1) // keep going back
                    }
                })

                router.go(-1) // now trigger the router hook
            }

            const functions = { localize, localizeBomItemText, onClick, materialText, goAllBack }

            // Share common functions with sub components
            provide('materialText', materialText)
            provide('onClick', onClick)

            /********************************************************************************************************************
             PRIVATE METHODS
             ********************************************************************************************************************/
            function addToMaterialHistory(materialData: MaterialListItem | null) {
                if (materialData != null) {
                    materialHistoryStore.addEntry(materialData)
                }
            }

            function scrollToTop() {
                const element = document.getElementById('top')
                if (element) {
                    element.scrollIntoView({
                        behavior: 'smooth'
                    })
                }
            }

            /** When traversing BOM data in partner login mode,
                materials might be found, that are not part of the active equipment.
                This method ensures to remove such materials.
            */
            function getEffectiveItems(items: MaterialListItem[] | undefined | null) {
                if (items == null) return null

                const equipmentMaterials = equipmentStore.bomItemQuantityDict

                if (isEquipmentMode.value && equipmentMaterials != null) {
                    // Restrict "Related items" to items that exist within the equipment BOM
                    return items.filter(item => item.materialNo != null && equipmentMaterials[item.materialNo] != null)
                }

                return items
            }

            function getQtyOnEquipment(): string | null {
                if (equipmentStore.bomItemQuantityDict == null) return null

                const bomQtyDto = equipmentStore.bomItemQuantityDict[materialNo.value]
                if (bomQtyDto == null) return null
                const { qty, unit } = bomQtyDto

                return qty != null && qty > 0
                    ? `${numberFormatter(qty)} ${localizeUnit3(unit)}`
                    : null
            }

            function collectParentMaterials(spareParts: IBomHeaderDTO[] | null): MaterialListItem[] | null {
                const parentMaterials = spareParts
                    ?.filter(part => isSparePartAndParentOf(part, materialNo.value))
                    .map(part => bomTransformer.transformSingle(part))
                    .filter(part => part != null)
                    .map(part => part as MaterialListItem)

                return getEffectiveItems(parentMaterials)
            }

            /** Specifies whether the specified parent material is a sparepart AND has any reference to the specified target material number */
            function isSparePartAndParentOf(parent: IBomHeaderDTO, targetMaterialNo: number,): boolean {
                return parent.material.isSparePart &&
                    parent.bomInstances
                        //.filter(bom => whereUsedParentBoms.includes(bom.bomUsage)) -- decision 2024-05-07: Use all BOM usages for "where used"
                        .some(bom => bom.components.some(comp => comp.material.materialNo == targetMaterialNo))
            }

            /********************************************************************************************************************
             INIT / LIFECYCLE HOOKS
             ********************************************************************************************************************/
            if (isEquipmentMode.value && !equipmentStore.activeSerialNo.value) {
                equipmentStore.setDevice(serialNo.value)
                equipmentStore.setActiveSerialNo(serialNo.value)
            }

            // update material history
            const materialHistoryStore = useMaterialHistoryStore()

            watchEffect(() => addToMaterialHistory(materialData.value))

            onBeforeUnmount(() => {
                if (unregisterGoAllBackHook != null) unregisterGoAllBackHook()
            })

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return {
                ...computedData,
                ...functions
            }
        }
    });
